import React, { useState, useMemo, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import FilterSidebar from '../components/FilterSidebar';

const Cart = () => {
  const { cart, loading, removeMCPServerFromCart, removeWorkflowFromCart, clearCart, addToActiveProfile, addMCPServerToActiveProfile, addWorkflowToActiveProfile } = useCart();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('servers');
  
  // Search and selection state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMCPServers, setSelectedMCPServers] = useState(new Set());
  const [selectedWorkflows, setSelectedWorkflows] = useState(new Set());
  const [selectAllServers, setSelectAllServers] = useState(false);
  const [selectAllWorkflows, setSelectAllWorkflows] = useState(false);
  
  // Filter and sort state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortBy, setSortBy] = useState('name');

  // Smart tab switching - switch to tab with content if current tab is empty
  useEffect(() => {
    if (cart && !loading) {
      const hasServers = cart.mcpServers && cart.mcpServers.length > 0;
      const hasWorkflows = cart.workflows && cart.workflows.length > 0;
      
      if (activeTab === 'servers' && !hasServers && hasWorkflows) {
        setActiveTab('workflows');
      } else if (activeTab === 'workflows' && !hasWorkflows && hasServers) {
        setActiveTab('servers');
      }
    }
  }, [cart, loading, activeTab]);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedMCPServers(new Set());
    setSelectedWorkflows(new Set());
    setSelectAllServers(false);
    setSelectAllWorkflows(false);
  }, [activeTab]);

  // Get unique categories and statuses from cart items
  const categories = useMemo(() => {
    const categoryMap = {};
    const allItems = [...(cart?.mcpServers || []), ...(cart?.workflows || [])];
    allItems.forEach(item => {
      if (item.category) {
        categoryMap[item.category] = (categoryMap[item.category] || 0) + 1;
      }
    });
    
    // Add "All" option at the beginning
    const allCategories = [
      { value: 'ALL', label: 'All', count: allItems.length }
    ];
    
    // Add specific categories
    Object.entries(categoryMap).forEach(([category, count]) => {
      allCategories.push({
        value: category,
        label: category,
        count
      });
    });
    
    return allCategories;
  }, [cart?.mcpServers, cart?.workflows]);

  const statuses = useMemo(() => {
    const statusMap = {};
    const allItems = [...(cart?.mcpServers || []), ...(cart?.workflows || [])];
    allItems.forEach(item => {
      statusMap[item.status] = (statusMap[item.status] || 0) + 1;
    });
    
    // Ensure both AVAILABLE and UNAVAILABLE are always present
    const allStatuses = [
      { value: 'AVAILABLE', label: 'Available', count: statusMap['AVAILABLE'] || 0 },
      { value: 'UNAVAILABLE', label: 'Unavailable', count: statusMap['UNAVAILABLE'] || 0 }
    ];
    
    return allStatuses;
  }, [cart?.mcpServers, cart?.workflows]);

  // Comprehensive filtering and sorting for cart items
  const filteredMCPServers = useMemo(() => {
    if (!cart?.mcpServers) return [];
    
    let filtered = [...cart.mcpServers];

    // Search filter - only search by name and description
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(server =>
        server.name.toLowerCase().includes(term) ||
        (server.description && server.description.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategories.length > 0 && !selectedCategories.includes('ALL')) {
      filtered = filtered.filter(server => 
        server.category && selectedCategories.includes(server.category)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(server => 
        selectedStatuses.includes(server.status)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'added':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [cart?.mcpServers, searchTerm, selectedCategories, selectedStatuses, sortBy]);

  const filteredWorkflows = useMemo(() => {
    if (!cart?.workflows) return [];
    
    let filtered = [...cart.workflows];

    // Search filter - only search by name and description
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(workflow =>
        workflow.name.toLowerCase().includes(term) ||
        (workflow.description && workflow.description.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategories.length > 0 && !selectedCategories.includes('ALL')) {
      filtered = filtered.filter(workflow => 
        workflow.category && selectedCategories.includes(workflow.category)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(workflow => 
        selectedStatuses.includes(workflow.status)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'added':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [cart?.workflows, searchTerm, selectedCategories, selectedStatuses, sortBy]);

  // Sync select all state when filtered items change
  useEffect(() => {
    setSelectAllServers(selectedMCPServers.size === filteredMCPServers.length && filteredMCPServers.length > 0);
  }, [selectedMCPServers, filteredMCPServers]);

  useEffect(() => {
    setSelectAllWorkflows(selectedWorkflows.size === filteredWorkflows.length && filteredWorkflows.length > 0);
  }, [selectedWorkflows, filteredWorkflows]);

  if (!user) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="card">
            <h2>Please log in to view your cart</h2>
            <p>You need to be logged in to access your cart.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-page" style={{ paddingTop: '0' }}>
        <div className="container">
          <div className="flex-center" style={{ height: '50vh' }}>
            <div>Loading cart...</div>
          </div>
        </div>
      </div>
    );
  }

  const handleRemoveMCPServer = async (serverId) => {
    const result = await removeMCPServerFromCart(serverId);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleRemoveWorkflow = async (workflowId) => {
    const result = await removeWorkflowFromCart(workflowId);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleClearCart = async () => {
    const result = await clearCart();
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleAddToProfile = async () => {
    if ((cart?.mcpServers?.length || 0) === 0 && (cart?.workflows?.length || 0) === 0) {
      alert('Your cart is empty');
      return;
    }

    const result = await addToActiveProfile();
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleAddMCPServerToProfile = async (serverId) => {
    const result = await addMCPServerToActiveProfile(serverId);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleAddWorkflowToProfile = async (workflowId) => {
    const result = await addWorkflowToActiveProfile(workflowId);
    if (!result.success) {
      alert(result.error);
    }
  };

  // Selection handlers
  const handleMCPServerSelect = (serverId, checked) => {
    const newSelected = new Set(selectedMCPServers);
    if (checked) {
      newSelected.add(serverId);
    } else {
      newSelected.delete(serverId);
    }
    setSelectedMCPServers(newSelected);
    
    // Update select all state
    setSelectAllServers(newSelected.size === filteredMCPServers.length);
  };

  const handleWorkflowSelect = (workflowId, checked) => {
    const newSelected = new Set(selectedWorkflows);
    if (checked) {
      newSelected.add(workflowId);
    } else {
      newSelected.delete(workflowId);
    }
    setSelectedWorkflows(newSelected);
    
    // Update select all state
    setSelectAllWorkflows(newSelected.size === filteredWorkflows.length);
  };

  const handleSelectAllMCPServers = (checked) => {
    setSelectAllServers(checked);
    if (checked) {
      setSelectedMCPServers(new Set(filteredMCPServers.map(server => server.id)));
    } else {
      setSelectedMCPServers(new Set());
    }
  };

  const handleSelectAllWorkflows = (checked) => {
    setSelectAllWorkflows(checked);
    if (checked) {
      setSelectedWorkflows(new Set(filteredWorkflows.map(workflow => workflow.id)));
    } else {
      setSelectedWorkflows(new Set());
    }
  };

  // Filter and sort handlers
  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSearchTerm('');
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'category', label: 'Category' },
    { value: 'added', label: 'Date Added' }
  ];


  const totalItems = (cart?.mcpServers?.length || 0) + (cart?.workflows?.length || 0);
  const filteredTotalItems = filteredMCPServers.length + filteredWorkflows.length;

  // Tab content rendering
  const renderTabContent = () => {
    switch (activeTab) {
      case 'servers':
        return (
          <div className="tab-content">
            {filteredMCPServers.length > 0 ? (
              <div>
                {/* Select All Section */}
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <div className="flex-between" style={{ alignItems: 'center' }}>
                    <div className="flex" style={{ alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectAllServers}
                        onChange={(e) => handleSelectAllMCPServers(e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      <label style={{ margin: 0, fontWeight: '500' }}>
                        Select All ({filteredMCPServers.length} servers)
                      </label>
                    </div>
                    {selectedMCPServers.size > 0 && (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ color: isDarkMode ? '#D1D5DB' : '#666', fontSize: '0.9rem' }}>
                          {selectedMCPServers.size} selected
                        </span>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            const serverIds = Array.from(selectedMCPServers);
                            serverIds.forEach(serverId => handleAddMCPServerToProfile(serverId));
                          }}
                        >
                          Add Selected to Profile
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            const serverIds = Array.from(selectedMCPServers);
                            serverIds.forEach(serverId => handleRemoveMCPServer(serverId));
                          }}
                        >
                          Remove Selected
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                  {filteredMCPServers.map((server) => (
                    <div key={server.id} style={{ 
                      padding: '1rem', 
                      border: '1px solid #eee', 
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: selectedMCPServers.has(server.id) ? (isDarkMode ? '#2a2a3e' : '#f8f9fa') : 'transparent'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedMCPServers.has(server.id)}
                        onChange={(e) => handleMCPServerSelect(server.id, e.target.checked)}
                        style={{ transform: 'scale(1.2)' }}
                      />
                        <div>
                          <h4 style={{ margin: '0 0 0.5rem 0' }}>{server.name}</h4>
                          <p style={{ color: '#666', margin: 0 }}>
                            {server.description || 'No description provided'}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAddMCPServerToProfile(server.id)}
                        >
                          Add to Profile
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleRemoveMCPServer(server.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <h3>No MCP Servers in Cart</h3>
                <p>Add some MCP servers to get started!</p>
                <a href="/mcp-servers"  className="btn btn-primary" style={{ marginTop: '1rem' }}>
                  Browse MCP Servers
                </a>
              </div>
            )}
          </div>
        );
      case 'workflows':
        return (
          <div className="tab-content">
            {filteredWorkflows.length > 0 ? (
              <div>
                {/* Select All Section */}
                <div className="card" style={{ marginBottom: '1rem' }}>
                  <div className="flex-between" style={{ alignItems: 'center' }}>
                    <div className="flex" style={{ alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectAllWorkflows}
                        onChange={(e) => handleSelectAllWorkflows(e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      <label style={{ margin: 0, fontWeight: '500' }}>
                        Select All ({filteredWorkflows.length} workflows)
                      </label>
                    </div>
                    {selectedWorkflows.size > 0 && (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ color: isDarkMode ? '#D1D5DB' : '#666', fontSize: '0.9rem' }}>
                          {selectedWorkflows.size} selected
                        </span>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            const workflowIds = Array.from(selectedWorkflows);
                            workflowIds.forEach(workflowId => handleAddWorkflowToProfile(workflowId));
                          }}
                        >
                          Add Selected to Profile
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            const workflowIds = Array.from(selectedWorkflows);
                            workflowIds.forEach(workflowId => handleRemoveWorkflow(workflowId));
                          }}
                        >
                          Remove Selected
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                  {filteredWorkflows.map((workflow) => (
                    <div key={workflow.id} style={{ 
                      padding: '1rem', 
                      border: '1px solid #eee', 
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: selectedWorkflows.has(workflow.id) ? (isDarkMode ? '#2a2a3e' : '#f8f9fa') : 'transparent'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedWorkflows.has(workflow.id)}
                        onChange={(e) => handleWorkflowSelect(workflow.id, e.target.checked)}
                        style={{ transform: 'scale(1.2)' }}
                      />
                        <div>
                          <h4 style={{ margin: '0 0 0.5rem 0' }}>{workflow.name}</h4>
                          <p style={{ color: '#666', margin: '0 0 0.5rem 0' }}>
                            {workflow.description || 'No description provided'}
                          </p>
                          {workflow.mcpServer && (
                            <p style={{ fontSize: '0.9rem', color: '#888', margin: 0 }}>
                              <strong>Associated Server:</strong> {workflow.mcpServer.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAddWorkflowToProfile(workflow.id)}
                        >
                          Add to Profile
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleRemoveWorkflow(workflow.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <h3>No Workflows in Cart</h3>
                <p>Add some workflows to get started!</p>
                <a href="/workflows"  className="btn btn-primary" style={{ marginTop: '1rem' }}>
                  Browse Workflows
                </a>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-page" style={{ paddingTop: '50px' }}>
      <div className="container">
        {/* Header Section with Logo and Title */}
        <div style={{ marginBottom: '3rem' }}>
          {/* Centered Title */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              color: isDarkMode ? 'white' : '#333'
            }}>
              Your Cart
            </h1>
          </div>
          
          {/* Subheader text - centered on the page */}
          <div style={{ 
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              fontSize: '1.2rem', 
              color: isDarkMode ? '#B0B0B0' : '#666',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Manage your selected MCP servers and workflows. Add items to your active profile or remove them from your cart as needed. Organize your research tools efficiently.
            </p>
          </div>
          
          {/* Cart count badge */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderRadius: '20px',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`
            }}>
              <span style={{ 
                color: isDarkMode ? 'white' : '#333',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                {totalItems} item{totalItems !== 1 ? 's' : ''} in cart
              </span>
            </div>
          </div>
        </div>

        {totalItems === 0 ? (
          <div className="card">
            <h3>Your cart is empty</h3>
            <p>Add some MCP servers or workflows to get started!</p>
            <div style={{ marginTop: '1rem' }}>
              <a href="/mcp-servers"  className="btn btn-primary" style={{ marginRight: '0.5rem' }}>
                Browse MCP Servers
              </a>
              <a href="/workflows"  className="btn btn-secondary">
                Browse Workflows
              </a>
            </div>
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="tab-navigation">
              <button
                className={`tab-button ${activeTab === 'servers' ? 'active' : ''}`}
                onClick={() => setActiveTab('servers')}
              >
                MCP Servers ({cart?.mcpServers?.length || 0})
              </button>
              <button
                className={`tab-button ${activeTab === 'workflows' ? 'active' : ''}`}
                onClick={() => setActiveTab('workflows')}
              >
                Workflows ({cart?.workflows?.length || 0})
              </button>
            </div>

            {/* Main Content with Sidebar */}
            <div className="mcp-servers-layout">
              {/* Left Side: Filter Sidebar */}
              <div className="filter-sidebar-wrapper">
                <FilterSidebar
                  categories={categories}
                  statuses={statuses}
                  selectedCategories={selectedCategories}
                  selectedStatuses={selectedStatuses}
                  onCategoryChange={handleCategoryChange}
                  onStatusChange={handleStatusChange}
                  onClearFilters={handleClearFilters}
                  title="Filters"
                />
              </div>

              {/* Right Side: Search, Sort, and Tab Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Search and Sort Controls */}
                <div className="search-sort-container" style={{
                  backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
                  border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
                  borderRadius: '8px',
                  padding: 'clamp(1rem, 3vw, 1.5rem)',
                  marginBottom: '1.5rem'
                }}>
                  {/* Search Element */}
                  <div className="search-wrapper" style={{ 
                    width: '300px', 
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      left: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#007bff',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by name or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem 0.75rem 2.5rem',
                        border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                        borderRadius: '8px',
                        backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                        color: isDarkMode ? 'white' : '#333',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#007bff';
                        e.target.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.25)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Sort Element - Anchored to the right */}
                  <div className="sort-wrapper" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    marginLeft: 'auto'
                  }}>
                    <span style={{
                      color: isDarkMode ? '#aaa' : '#666',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      Showing {filteredTotalItems} of {totalItems}
                    </span>
                    
                    <div style={{ position: 'relative' }}>
                      <button
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                          border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                          borderRadius: '6px',
                          color: isDarkMode ? 'white' : '#333',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                          e.target.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#2a2a2a' : 'white';
                          e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                        }}
                      >
                        Sort by {sortOptions.find(opt => opt.value === sortBy)?.label || 'popularity'}
                        <span>▼</span>
                      </button>
                      
                      {/* Dropdown Menu */}
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                        border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '150px',
                        marginTop: '0.25rem',
                        display: 'none' // This would be controlled by state in a real implementation
                      }}>
                        {sortOptions.map((option) => (
                          <div
                            key={option.value}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              color: isDarkMode ? 'white' : '#333',
                              fontSize: '0.9rem',
                              borderBottom: `1px solid ${isDarkMode ? '#333' : '#f0f0f0'}`,
                              transition: 'background-color 0.2s ease'
                            }}
                            onClick={() => handleSortChange(option.value)}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                {renderTabContent()}
              </div>
            </div>

            {/* Cart Actions */}
            <div className="card" style={{ marginTop: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleClearCart}
                >
                  Clear Cart
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddToProfile}
                >
                  Add All to Active Profile
                </button>
              </div>
            </div>

            {/* No search results */}
            {totalItems > 0 && filteredTotalItems === 0 && searchTerm.trim() && (
              <div className="card">
                <h3>No results found</h3>
                <p>No items match your search term "{searchTerm}". Try adjusting your search.</p>
                <button
                  className="btn btn-secondary"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;
